/**
 * WebSocket connection hook for dashboard real-time event updates
 *
 * Manages WebSocket connection lifecycle with:
 * - JWT token authentication via query parameter
 * - Connection status tracking
 * - Exponential backoff reconnection (1s initial, 30s max, 10 attempts)
 * - Simplified event handling for event.created and event.updated only
 *
 * Adapted from internal-testing-tool hook with simplified event filtering
 * (server handles tenant filtering via org_code stored with connection)
 */

import type { EventCreatedEvent, EventUpdatedEvent } from "@sitescene/types";

import {
	EVENT_CREATED_DETAIL_TYPE,
	EVENT_UPDATED_DETAIL_TYPE,
} from "@sitescene/types";
import {
	useCallback,
	useEffect,
	useLayoutEffect,
	useRef,
	useState,
} from "react";

import { getAccessToken } from "@/api/token-manager";

/**
 * WebSocket connection status
 */
export type ConnectionStatus =
	| "connected"
	| "connecting"
	| "disconnected"
	| "error";

/**
 * Union type for dashboard WebSocket events (only event.created and event.updated)
 */
export type DashboardWebSocketEvent =
	| EventCreatedWebSocketEvent
	| EventUpdatedWebSocketEvent;

/**
 * Event types handled by the dashboard WebSocket
 */
export type DashboardWebSocketEventType = "event.created" | "event.updated";

/**
 * event.created event structure from EventBridge via WebSocket
 */
export interface EventCreatedWebSocketEvent {
	detail: EventCreatedEvent;
	"detail-type": typeof EVENT_CREATED_DETAIL_TYPE;
	source: string;
}

/**
 * event.updated event structure from EventBridge via WebSocket
 */
export interface EventUpdatedWebSocketEvent {
	detail: EventUpdatedEvent;
	"detail-type": typeof EVENT_UPDATED_DETAIL_TYPE;
	source: string;
}

/**
 * Parsed event with metadata
 */
export interface ParsedDashboardEvent {
	event: DashboardWebSocketEvent;
	id: string;
	receivedAt: Date;
	type: DashboardWebSocketEventType;
}

/**
 * Hook configuration options
 */
export interface UseWebSocketConnectionOptions {
	/** Maximum reconnection attempts (default: 10) */
	maxReconnectAttempts?: number;
	/** Callback when an event is received */
	onEvent?: (event: ParsedDashboardEvent) => void;
	/** WebSocket URL to connect to */
	websocketUrl: string;
}

/**
 * Hook return value
 */
export interface UseWebSocketConnectionResult {
	/** Manually disconnect the WebSocket */
	disconnect: () => void;
	/** Error message if connection failed */
	error: null | string;
	/** Manually trigger reconnection */
	reconnect: () => void;
	/** Current connection status */
	status: ConnectionStatus;
}

/**
 * Type guard to check if an event is an EventCreated event
 */
export function isEventCreatedEvent(
	event: DashboardWebSocketEvent,
): event is EventCreatedWebSocketEvent {
	return event["detail-type"] === EVENT_CREATED_DETAIL_TYPE;
}

/**
 * Type guard to check if an event is an EventUpdated event
 */
export function isEventUpdatedEvent(
	event: DashboardWebSocketEvent,
): event is EventUpdatedWebSocketEvent {
	return event["detail-type"] === EVENT_UPDATED_DETAIL_TYPE;
}

/**
 * Parse the event type from a WebSocket event
 */
export function parseEventType(
	event: DashboardWebSocketEvent,
): DashboardWebSocketEventType | null {
	const detailType = event["detail-type"];

	switch (detailType) {
		case EVENT_CREATED_DETAIL_TYPE:
			return "event.created";
		case EVENT_UPDATED_DETAIL_TYPE:
			return "event.updated";
		default:
			return null;
	}
}

/**
 * Generate a unique ID for an event
 */
function generateEventId(): string {
	return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
}

/**
 * Initial reconnection delay in milliseconds
 */
const INITIAL_RECONNECT_DELAY = 1000;

/**
 * Maximum reconnection delay in milliseconds
 */
const MAX_RECONNECT_DELAY = 30000;

/**
 * WebSocket close code for normal closure
 */
const NORMAL_CLOSURE_CODE = 1000;

/**
 * Hook for managing WebSocket connection with JWT authentication
 * for dashboard real-time event updates.
 *
 * Server-side filtering by tenant (org_code) means client only receives
 * events belonging to the user's organization.
 */
export function useWebSocketConnection(
	options: UseWebSocketConnectionOptions,
): UseWebSocketConnectionResult {
	const { maxReconnectAttempts = 10, onEvent, websocketUrl } = options;

	const [status, setStatus] = useState<ConnectionStatus>("connecting");
	const [error, setError] = useState<null | string>(null);

	// Refs for managing connection state
	const wsRef = useRef<null | WebSocket>(null);
	const reconnectAttemptRef = useRef(0);
	const reconnectTimeoutRef = useRef<null | ReturnType<typeof setTimeout>>(
		null,
	);
	const isManualDisconnectRef = useRef(false);
	// Ref to store connect function for self-referencing in reconnection logic
	const connectRef = useRef<(() => Promise<void>) | null>(null);
	// Ref for onEvent to avoid recreating connect when callback changes
	const onEventRef = useRef(onEvent);
	// Unique ID for this mount cycle - used to cancel stale async operations from previous mounts
	const mountIdRef = useRef(0);

	// Keep onEvent ref up to date
	useEffect(() => {
		onEventRef.current = onEvent;
	}, [onEvent]);

	/**
	 * Clear pending reconnection timeout
	 */
	const clearReconnectTimeout = useCallback(() => {
		if (reconnectTimeoutRef.current) {
			clearTimeout(reconnectTimeoutRef.current);
			reconnectTimeoutRef.current = null;
		}
	}, []);

	/**
	 * Connect to WebSocket with JWT token
	 */
	const connect = useCallback(async () => {
		// Clear any pending reconnection
		clearReconnectTimeout();

		// Close existing connection if any
		if (wsRef.current) {
			wsRef.current.close();
			wsRef.current = null;
		}

		// Capture mount ID at start of this connection attempt
		const currentMountId = mountIdRef.current;

		setStatus("connecting");
		setError(null);

		// If no WebSocket URL is provided, skip connection
		if (!websocketUrl || websocketUrl.trim() === "") {
			setStatus("disconnected");
			return;
		}

		try {
			// Get JWT token from token manager
			const token = await getAccessToken();

			// Check if mount ID changed during async operation (StrictMode remount)
			if (mountIdRef.current !== currentMountId) {
				return;
			}

			if (!token) {
				setStatus("error");
				setError("Failed to get access token");
				return;
			}

			// Create WebSocket with token in query string
			const url = `${websocketUrl}?token=${token}`;
			const ws = new WebSocket(url);
			wsRef.current = ws;

			ws.onopen = () => {
				setStatus("connected");
				setError(null);
				// Reset reconnection attempts on successful connection
				reconnectAttemptRef.current = 0;
			};

			ws.onmessage = (event) => {
				try {
					const data = JSON.parse(event.data) as DashboardWebSocketEvent;
					const eventType = parseEventType(data);

					// Only handle event.created and event.updated
					if (!eventType) {
						return;
					}

					const parsedEvent: ParsedDashboardEvent = {
						event: data,
						id: generateEventId(),
						receivedAt: new Date(),
						type: eventType,
					};

					// Call onEvent callback if provided (using ref to avoid stale closure)
					if (onEventRef.current) {
						onEventRef.current(parsedEvent);
					}
				} catch (err) {
					console.error("Failed to parse WebSocket message:", err);
				}
			};

			ws.onerror = () => {
				setError("WebSocket connection error");
			};

			ws.onclose = (event) => {
				setStatus("disconnected");
				wsRef.current = null;

				// Don't reconnect on normal closure or manual disconnect
				if (
					event.code === NORMAL_CLOSURE_CODE ||
					isManualDisconnectRef.current
				) {
					isManualDisconnectRef.current = false;
					return;
				}

				// Attempt reconnection with exponential backoff
				if (reconnectAttemptRef.current < maxReconnectAttempts) {
					const delay = Math.min(
						INITIAL_RECONNECT_DELAY * Math.pow(2, reconnectAttemptRef.current),
						MAX_RECONNECT_DELAY,
					);

					reconnectAttemptRef.current += 1;

					reconnectTimeoutRef.current = setTimeout(() => {
						connectRef.current?.();
					}, delay);
				} else {
					setStatus("error");
					setError("Max reconnection attempts reached");
				}
			};
		} catch (err) {
			setStatus("error");
			setError(
				err instanceof Error ? err.message : "Failed to establish connection",
			);
		}
	}, [clearReconnectTimeout, maxReconnectAttempts, websocketUrl]);

	// Store connect in ref for self-referencing in reconnection logic. Layout effect so the
	// ref is current before any passive effect (including connect-on-mount) reads it.
	useLayoutEffect(() => {
		connectRef.current = connect;
	}, [connect]);

	/**
	 * Manually disconnect WebSocket
	 */
	const disconnect = useCallback(() => {
		isManualDisconnectRef.current = true;
		clearReconnectTimeout();

		if (wsRef.current) {
			wsRef.current.close(NORMAL_CLOSURE_CODE, "Manual disconnect");
			wsRef.current = null;
		}

		setStatus("disconnected");
	}, [clearReconnectTimeout]);

	/**
	 * Manually trigger reconnection
	 */
	const reconnect = useCallback(() => {
		isManualDisconnectRef.current = false;
		reconnectAttemptRef.current = 0;
		connect();
	}, [connect]);

	// Connect on mount (only once)
	useEffect(() => {
		// Increment mount ID for this mount cycle
		mountIdRef.current += 1;

		// Use the ref to call connect, avoiding dependency on connect callback
		connectRef.current?.();

		return () => {
			// Increment mount ID again to invalidate any in-flight async operations
			mountIdRef.current += 1;
			clearReconnectTimeout();
			if (wsRef.current) {
				wsRef.current.close(NORMAL_CLOSURE_CODE, "Component unmount");
				wsRef.current = null;
			}
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps -- Intentional: only run on mount/unmount
	}, []);

	return {
		disconnect,
		error,
		reconnect,
		status,
	};
}
